import { useEffect, useSyncExternalStore } from "react";
import posthog from "posthog-js";
import { toast } from "sonner";
import {
  SAME_AS_LEAD_CAST_PHOTO_COPY,
  castIdentityForCast,
  castPhotoCheckKey,
  castPhotoSameAsLeadCheckApplies,
  sameAsLeadCastPhotoNote,
  withCastIdentityAnswer,
  type CastIdentityAnswer,
  type CastIdentityEntry,
  type CastIdentityMap,
} from "@shared/castIdentity";

/**
 * The web composers' side of "It's someone else" (shared/castIdentity.ts):
 * the upload-time same-person check, run once per photo and main photo per
 * page session and shared by the reference picker and every composer, and
 * the user's answers, which ride on each reference until createProject.
 */

export type CheckSameAsLead = (input: {
  imageUrl: string;
  leadImageUrl: string;
}) => Promise<{ sameAsLead: boolean | null }>;

/** The slice of a composer reference this needs. */
export type CastIdentityReference = {
  imageUrl: string;
  role: "character" | "vibe";
  /** The user's answer about this photo, against the main photo it was
   *  given for. */
  castIdentity?: CastIdentityEntry;
};

const verdicts = new Map<string, boolean | null>();
const pending = new Map<string, Promise<boolean | null>>();
const listeners = new Set<() => void>();
let version = 0;

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** The check's verdict for `imageUrl` against the main photo: undefined =
 *  not checked yet, null = no verdict. */
export function sameAsLeadVerdict(
  imageUrl: string,
  leadImageUrl: string,
): boolean | null | undefined {
  return verdicts.get(castPhotoCheckKey(imageUrl, leadImageUrl));
}

/** Checks a photo against a main photo once; later calls share the first
 *  call's result. A failed call is a null verdict, never a throw. */
export function checkSameAsLeadOnce(
  imageUrl: string,
  leadImageUrl: string,
  check: CheckSameAsLead,
): Promise<boolean | null> {
  const key = castPhotoCheckKey(imageUrl, leadImageUrl);
  if (verdicts.has(key)) return Promise.resolve(verdicts.get(key) ?? null);
  const inFlight = pending.get(key);
  if (inFlight) return inFlight;
  const request = check({ imageUrl, leadImageUrl })
    .then(({ sameAsLead }) => (typeof sameAsLead === "boolean" ? sameAsLead : null))
    .catch(() => null)
    .then((verdict) => {
      verdicts.set(key, verdict);
      pending.delete(key);
      version += 1;
      listeners.forEach((listener) => listener());
      return verdict;
    });
  pending.set(key, request);
  return request;
}

/** Runs the check for each pair not checked yet and re-renders as verdicts
 *  land. `check` null (signed out) runs nothing. */
export function useSameAsLeadVerdicts(
  pairs: ReadonlyArray<{ imageUrl: string; leadImageUrl: string }>,
  check: CheckSameAsLead | null,
): typeof sameAsLeadVerdict {
  useSyncExternalStore(
    subscribe,
    () => version,
    () => version,
  );
  const pairsKey = pairs
    .map((pair) => castPhotoCheckKey(pair.imageUrl, pair.leadImageUrl))
    .join("\n\n");
  useEffect(() => {
    if (!check) return;
    for (const pair of pairs) {
      void checkSameAsLeadOnce(pair.imageUrl, pair.leadImageUrl, check);
    }
    // pairsKey stands for pairs.
  }, [pairsKey, check]);
  return sameAsLeadVerdict;
}

/** The main photo of a composer cast: its first character reference. */
export function referenceLeadUrl(
  references: readonly CastIdentityReference[],
): string | undefined {
  return references.find((r) => r.role === "character")?.imageUrl;
}

/** The answers carried on the references, keyed by photo. */
export function castIdentityMapFromReferences(
  references: readonly CastIdentityReference[],
): CastIdentityMap {
  const map: CastIdentityMap = {};
  for (const r of references) {
    if (r.castIdentity) map[r.imageUrl] = r.castIdentity;
  }
  return map;
}

/** `references` with the answer about `imageUrl` next to `leadImageUrl` set
 *  (the pair's other-way-round answer, if any, is dropped). */
export function withReferenceCastIdentity<T extends CastIdentityReference>(
  references: readonly T[],
  imageUrl: string,
  leadImageUrl: string,
  identity: CastIdentityAnswer,
): T[] {
  const map = withCastIdentityAnswer(
    castIdentityMapFromReferences(references),
    imageUrl,
    leadImageUrl,
    identity,
  );
  return references.map((r) => {
    const entry = Object.prototype.hasOwnProperty.call(map, r.imageUrl)
      ? map[r.imageUrl]
      : undefined;
    if (entry === r.castIdentity) return r;
    if (entry) return { ...r, castIdentity: entry };
    const next = { ...r };
    delete next.castIdentity;
    return next;
  });
}

/** createProject's castIdentity for a cast of character references (the
 *  first one leads); undefined when the user answered nothing. */
export function referenceCastIdentity(
  characters: readonly CastIdentityReference[],
): CastIdentityMap | undefined {
  const answers = castIdentityForCast(
    castIdentityMapFromReferences(characters),
    characters[0]?.imageUrl,
    characters.slice(1).map((c) => c.imageUrl),
  );
  return Object.keys(answers).length ? answers : undefined;
}

/**
 * Photos picked while signed out are uploaded after sign-in without ever
 * showing in the picker: check the just-uploaded `imageUrl` against the main
 * photo and, on a match, toast the note with an "It's someone else" action.
 */
export function toastSameAsLeadCastPhoto(input: {
  imageUrl: string;
  /** The composer's latest references. */
  references: () => readonly CastIdentityReference[];
  check: CheckSameAsLead;
  onSomeoneElse: (leadImageUrl: string) => void;
  /** Analytics surface. */
  surface: string;
}): void {
  const references = input.references();
  const lead = referenceLeadUrl(references);
  if (!lead || !castPhotoSameAsLeadCheckApplies(input.imageUrl, lead)) return;
  void checkSameAsLeadOnce(input.imageUrl, lead, input.check).then(
    (sameAsLead) => {
      const latest = input.references();
      // Still the same pair: the photo is an extra person next to this lead.
      if (
        referenceLeadUrl(latest) !== lead ||
        !latest.some(
          (r) => r.imageUrl === input.imageUrl && r.role === "character",
        )
      ) {
        return;
      }
      const note = sameAsLeadCastPhotoNote({
        imageUrl: input.imageUrl,
        leadImageUrl: lead,
        sameAsLead,
        answers: castIdentityMapFromReferences(latest),
      });
      if (!note || note.someoneElse) return;
      posthog.capture("character_photo_same_as_lead_note", {
        surface: input.surface,
      });
      toast(SAME_AS_LEAD_CAST_PHOTO_COPY.note, {
        duration: 12000,
        action: {
          label: SAME_AS_LEAD_CAST_PHOTO_COPY.someoneElse,
          onClick: () => {
            posthog.capture("character_photo_same_as_lead_answer", {
              surface: input.surface,
              identity: "someone_else",
            });
            input.onSomeoneElse(lead);
          },
        },
      });
    },
  );
}
